import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { BRAND_COLORS, Icon, Row, Text, TextVariant } from "@bridgesplit/ui";
import { colorToAlpha, formatOrDash, formatPercent } from "@bridgesplit/utils";
import { Box, useTheme } from "@mui/material";
import { OverlappingMetadataImages } from "app/components/common";

const MAX_ICON_TOKENS = 3;

export function EarnVaultIcon({ metadata, size = 32 }: { metadata: TokenListMetadata[] | undefined; size?: number }) {
    const { palette } = useTheme();
    const tint = colorToAlpha(palette.secondary.main, 0.1);
    const tokens = metadata?.slice(0, MAX_ICON_TOKENS);
    const tokenImageSize = getTokenImageSize(size, tokens?.length ?? 1);

    return (
        <Box
            sx={{
                height: size,
                width: size,
                borderRadius: "50%",
                bgcolor: "background.default",
                backgroundImage: `linear-gradient(${tint}, ${tint})`,
                alignContent: "center",
                justifyItems: "center"
            }}
        >
            <OverlappingMetadataImages
                sort={false}
                disableTooltip
                size={`${tokenImageSize}px`}
                metadata={tokens}
                overlap={tokenImageSize / 3}
                outline={false}
                sx={{ bgcolor: BRAND_COLORS.LIGHT }}
            />
        </Box>
    );
}

// Overlapping tokens span (2n + 1) / 3 token widths; keep that inside three quarters of the circle.
function getTokenImageSize(size: number, count: number) {
    return Math.min((size * 3) / 8, Math.floor((size * 0.75 * 3) / (2 * count + 1)));
}

export function EarnBoostedYieldIcon({
    rewardApy,
    variant = "body1"
}: {
    rewardApy: number | undefined;
    variant?: TextVariant;
}) {
    if (!rewardApy) return null;
    return (
        <Text variant={variant} color="secondary">
            <Icon type="boosted-yield" />
        </Text>
    );
}

export function EarnHeadlineApy({
    headlineApy,
    rewardApy,
    variant = "body1"
}: {
    headlineApy: number | undefined;
    rewardApy: number | undefined;
    variant?: TextVariant;
}) {
    return (
        <Row spacing={0.5}>
            <EarnBoostedYieldIcon rewardApy={rewardApy} variant={variant} />
            <Text variant={variant} translate="no">
                {formatOrDash(headlineApy, () => formatPercent(headlineApy, { customDecimals: 2 }))}
            </Text>
        </Row>
    );
}
